import { useState, useEffect, useMemo } from 'react';
import { CustomerEquipmentTypeResponse } from '@/types';
import { getAvailableTiers, hasTieredPricing } from '@/lib/pricing';
import { useFormatPrice } from '@/hooks/useFormatPrice';
import { Check, Calendar, AlertTriangle, Edit2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { format, addDays } from 'date-fns';

interface SelectedTierInfo {
    tierName: string;
    minDays: number;
    maxDays: number;
    price: number;
}

interface RentalDurationSelectorProps {
    product: CustomerEquipmentTypeResponse;
    /** Pre-selected tier based on fixed dates */
    preSelectedDays?: number | null;
    /** Called when user selects a tier */
    onTierSelect: (tier: SelectedTierInfo) => void;
    /** Called when user sets pickup date (in flexible mode) */
    onPickupDateChange?: (date: Date | null) => void;
    /**
     * Called when the user explicitly chooses a specific return date inside the
     * tier window via the disclosure. `null` means the user reset to the tier
     * default (parent should clear any explicit return-date state).
     */
    onReturnDateChange?: (date: Date | null) => void;
    /** Pre-filled pickup date */
    pickupDate?: Date | null;
    /** Whether we're in fixed dates mode (dates already set) */
    fixedDatesMode?: boolean;
    /** Pre-set return date for fixed mode */
    returnDate?: Date | null;
    /** Initial tier selection from store (for flexible mode persistence) */
    initialTier?: SelectedTierInfo | null;
    /** Externally controlled selection (for mutual exclusivity with subscription) */
    isSelected?: boolean;
    /** Called when user clicks to update their dates */
    onUpdateDatesClick?: () => void;
    /** Whether location is required before selecting */
    requiresLocation?: boolean;
    /** Called when location is required but not set */
    onLocationRequired?: () => void;
    /** Whether the customer selected brand new item (uses premium pricing) */
    isNewItem?: boolean;
    className?: string;
}

/**
 * Rental Duration Selector
 *
 * Shows tier buttons for selecting rental duration.
 * - In fixed dates mode: auto-selects the appropriate tier, shows dates
 * - In flexible mode: lets user pick a tier, then asks for pickup date
 */
export const RentalDurationSelector = ({
    product,
    preSelectedDays,
    onTierSelect,
    onPickupDateChange,
    onReturnDateChange,
    pickupDate,
    fixedDatesMode = false,
    returnDate,
    initialTier,
    isSelected = true,
    onUpdateDatesClick,
    requiresLocation = false,
    onLocationRequired,
    isNewItem = false,
    className
}: RentalDurationSelectorProps) => {
    const formatPrice = useFormatPrice();
    const availableTiers = useMemo(() => getAvailableTiers(product, product, isNewItem), [product, isNewItem]);
    const [selectedTier, setSelectedTier] = useState<SelectedTierInfo | null>(initialTier ?? null);
    const [showPickupPicker, setShowPickupPicker] = useState(!fixedDatesMode && !pickupDate);
    const [showReturnDatePicker, setShowReturnDatePicker] = useState(false);

    // Clear internal selection when externally deselected (e.g., subscription selected)
    useEffect(() => {
        if (!isSelected && selectedTier) {
            setSelectedTier(null);
        }
    }, [isSelected, selectedTier]);

    useEffect(() => {
        if (fixedDatesMode) {
            setShowPickupPicker(false);
            return;
        }

        if (pickupDate) {
            setShowPickupPicker(false);
        }
    }, [fixedDatesMode, pickupDate]);

    // Auto-select tier based on pre-selected days (fixed dates mode)
    useEffect(() => {
        if (preSelectedDays && fixedDatesMode) {
            // Find the matching tier from available tiers
            const availableTier = availableTiers.find(t =>
                preSelectedDays >= t.minDays && preSelectedDays <= t.maxDays
            );
            if (availableTier) {
                const tierInfo: SelectedTierInfo = {
                    tierName: availableTier.tierName,
                    minDays: availableTier.minDays,
                    maxDays: availableTier.maxDays,
                    price: availableTier.price
                };
                setSelectedTier(tierInfo);
            }
        }
    }, [preSelectedDays, fixedDatesMode, availableTiers]);

    if (!hasTieredPricing(product) || availableTiers.length === 0) {
        return null;
    }

    const handleTierClick = (tier: typeof availableTiers[0]) => {
        // Check if location is required but not set
        if (requiresLocation && onLocationRequired) {
            onLocationRequired();
            return;
        }

        const tierInfo: SelectedTierInfo = {
            tierName: tier.tierName,
            minDays: tier.minDays,
            maxDays: tier.maxDays,
            price: tier.price
        };
        setSelectedTier(tierInfo);
        onTierSelect(tierInfo);

        // In flexible mode, only ask for pickup date when we don't already have one.
        if (!fixedDatesMode) {
            setShowPickupPicker(!pickupDate);
        }
    };

    const handlePickupDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Use local noon (no Z suffix) so .toISOString() stays on the same calendar date in any timezone
        const date = e.target.value ? new Date(`${e.target.value}T12:00:00`) : null;
        onPickupDateChange?.(date);
        if (date) {
            setShowPickupPicker(false);
        }
    };

    const handleReturnDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.value) return;
        // Local noon to keep the calendar date stable across timezones — same approach as pickup.
        const date = new Date(`${e.target.value}T12:00:00`);
        onReturnDateChange?.(date);
    };

    const handleResetReturnDate = () => {
        setShowReturnDatePicker(false);
        onReturnDateChange?.(null);
    };

    // Format date for input
    const formatDateForInput = (date: Date | null | undefined) => {
        if (!date) return '';
        return format(date, 'yyyy-MM-dd');
    };

    const minReturnDate = pickupDate && selectedTier
        ? addDays(pickupDate, selectedTier.minDays)
        : null;
    const maxReturnDate = pickupDate && selectedTier
        ? addDays(pickupDate, selectedTier.maxDays)
        : null;

    return (
        <div className={cn("space-y-4", className)}>
            {/* Section Header */}
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-foreground">
                    {fixedDatesMode ? 'Your rental duration' : 'How long do you need it?'}
                </h3>
                {selectedTier && (
                    <span className="text-lg font-bold text-primary">
                        {formatPrice(selectedTier.price)}
                    </span>
                )}
            </div>

            {/* Tier Buttons */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                {availableTiers.map((tier) => {
                    const isSelected = selectedTier?.tierName === tier.tierName;
                    // Check if this tier matches the pre-selected days (from fixed dates)
                    const matchesFixedDates = preSelectedDays != null &&
                        preSelectedDays >= tier.minDays &&
                        preSelectedDays <= tier.maxDays;
                    const showMatchBadge = fixedDatesMode && matchesFixedDates;

                    return (
                        <button
                            key={tier.tierName}
                            onClick={() => handleTierClick(tier)}
                            className={cn(
                                "relative flex flex-col items-center justify-center p-2 rounded-md border-2 transition-all text-center min-h-[52px]",
                                isSelected
                                    ? "border-primary bg-primary/5 shadow-sm"
                                    : "border-border hover:border-primary/50 hover:bg-muted"
                            )}
                            type="button"
                            aria-label={`${tier.tierName}: ${formatPrice(tier.price)}`}
                        >
                            <span className={cn(
                                "text-xs font-semibold",
                                isSelected ? "text-primary" : "text-foreground"
                            )}>
                                {tier.tierName}
                            </span>
                            {isSelected && (
                                <div className="absolute top-1 right-1">
                                    <Check className="h-3.5 w-3.5 text-primary" />
                                </div>
                            )}
                            {showMatchBadge && (
                                <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-primary text-white text-[10px] font-medium rounded-full">
                                    Your dates
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Selected Tier Summary */}
            {selectedTier && (
                <div className="p-4 bg-muted rounded-lg border border-border space-y-3">
                    {/* Price Display */}
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">
                            {selectedTier.tierName} rental
                        </span>
                        <span className="text-xl font-bold text-foreground">
                            {formatPrice(selectedTier.price)}
                        </span>
                    </div>

                    {/* Warning if selected tier doesn't match the fixed dates */}
                    {fixedDatesMode && preSelectedDays != null && (
                        preSelectedDays < selectedTier.minDays || preSelectedDays > selectedTier.maxDays
                    ) && (
                        <div className="p-3 bg-warning-subtle border border-warning-border rounded-lg">
                            <div className="flex items-start gap-2">
                                <AlertTriangle className="h-4 w-4 text-warning flex-shrink-0 mt-0.5" />
                                <div className="text-sm flex-1">
                                    <p className="text-warning font-medium">Dates don't match this tier</p>
                                    <p className="text-warning mt-0.5">
                                        Your selected dates ({preSelectedDays} days) don't fit the {selectedTier.tierName} tier ({selectedTier.minDays}-{selectedTier.maxDays} days).
                                    </p>
                                    {onUpdateDatesClick && (
                                        <button
                                            type="button"
                                            onClick={onUpdateDatesClick}
                                            className="mt-2 inline-flex items-center gap-1.5 text-warning hover:text-warning font-medium underline underline-offset-2"
                                        >
                                            <Edit2 className="h-3.5 w-3.5" />
                                            Update your dates
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Fixed Dates Mode - Show the dates */}
                    {fixedDatesMode && returnDate && pickupDate && (
                        <div className="pt-3 border-t border-border">
                            <div className="flex items-center gap-2 text-sm">
                                <Calendar className="h-4 w-4 text-muted-foreground/70" />
                                <span className="text-muted-foreground">
                                    {format(pickupDate, 'MMM d')} → {format(returnDate, 'MMM d, yyyy')}
                                </span>
                                <span className="text-muted-foreground/70">
                                    ({preSelectedDays} days)
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Flexible Mode */}
                    {!fixedDatesMode && (
                        <div className="pt-3 border-t border-border space-y-3">
                            {pickupDate && !showPickupPicker && (
                                <div className="p-3 bg-primary/5 rounded-lg text-sm">
                                    <div className="flex items-center gap-2 text-primary">
                                        <Calendar className="h-4 w-4" />
                                        <span className="font-medium">Pickup date confirmed</span>
                                    </div>
                                    <p className="mt-1 text-primary">
                                        Pickup on {format(pickupDate, 'EEEE, MMMM d, yyyy')}
                                    </p>
                                    {minReturnDate && maxReturnDate && (
                                        <p className="mt-1 text-xs text-primary">
                                            Return window for this tier: {format(minReturnDate, 'MMM d')} - {format(maxReturnDate, 'MMM d, yyyy')}
                                        </p>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => setShowPickupPicker(true)}
                                        className="mt-2 inline-flex items-center gap-1.5 text-primary hover:text-primary font-medium underline underline-offset-2"
                                    >
                                        <Edit2 className="h-3.5 w-3.5" />
                                        Change pickup date
                                    </button>
                                </div>
                            )}

                            {(!pickupDate || showPickupPicker) && (
                                <div>
                                    <label htmlFor="pickup-date" className="block text-sm font-medium text-foreground mb-1">
                                        Pickup date
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="date"
                                            id="pickup-date"
                                            value={formatDateForInput(pickupDate)}
                                            onChange={handlePickupDateChange}
                                            min={formatDateForInput(new Date())}
                                            className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-primary text-sm"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Optional: pick a specific return date inside the tier window */}
                            {pickupDate && minReturnDate && maxReturnDate && (
                                <div>
                                    {!showReturnDatePicker ? (
                                        <button
                                            type="button"
                                            onClick={() => setShowReturnDatePicker(true)}
                                            className="text-xs font-medium text-primary hover:text-primary underline underline-offset-2"
                                        >
                                            + Choose a specific return date
                                        </button>
                                    ) : (
                                        <div>
                                            <label htmlFor="return-date" className="block text-sm font-medium text-foreground mb-1">
                                                Return date
                                            </label>
                                            <input
                                                type="date"
                                                id="return-date"
                                                value={formatDateForInput(returnDate ?? minReturnDate)}
                                                onChange={handleReturnDateChange}
                                                min={formatDateForInput(minReturnDate)}
                                                max={formatDateForInput(maxReturnDate)}
                                                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-primary text-sm"
                                            />
                                            <button
                                                type="button"
                                                onClick={handleResetReturnDate}
                                                className="mt-2 text-xs font-medium text-muted-foreground hover:text-foreground underline underline-offset-2"
                                            >
                                                Reset to tier default
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                </div>
            )}

            {/* Helper text when no tier selected */}
            {!selectedTier && !fixedDatesMode && (
                <p className="text-xs text-muted-foreground text-center">
                    Select a rental duration to see the price
                </p>
            )}
        </div>
    );
};

export default RentalDurationSelector;
